import React, { useEffect, useState } from "react";
import { ModalX } from "../../components";
import { Button, Checkbox, Input, Option, Select, Spinner } from "@material-tailwind/react";
import { convertToPresentableText, getOrdinal, NotifyError } from "../../utils/functions";
import { LEVELS, LEVELS_ENUM, OPERATIONAL_STATUSES, OPERATIONAL_STATUSES_ENUM, SECURITY_TYPES, SECURITY_TYPES_ENUM } from "../../utils/enums";
import { useGetFloors } from "../../custom-hooks/use-floors";
import { useAddCoworkingSpace, useUpdateCoworkingSpace } from "../../custom-hooks/use-coworking-spaces";

const INITIAL_FORM_DATA = {
    floor: '',
    number: '',
    code: '',
    // capacity: '',
    useableArea: '',
    totalArea: '',
    status: 'operational',
    securityType: '',
    securityLevel: '',
    isAccessible: true,
    isBookable: true,
    dedicatedClimateControl: true,
    dedicatedInternet: false
};

/**
 * Self-contained create/update modal for coworking spaces.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object|null} [props.space] - null/undefined for create, space object for edit
 * @param {string} [props.defaultFloorId] - optional floor id to preselect (and lock) on create
 * @param {() => void} [props.onSuccess] - called after successful add/update
 */
const CoworkingCrudModal = ({
    isOpen,
    onClose,
    space = null,
    defaultFloorId,
    onSuccess,
}) => {
    const isUpdating = Boolean(space);
    const [formData, setFormData] = useState(INITIAL_FORM_DATA);

    const { data: floors, isLoading: isFloorsLoading, isError: isFloorsError } = useGetFloors();
    const addCoworkingSpaceMutation = useAddCoworkingSpace();
    const updateCoworkingSpaceMutation = useUpdateCoworkingSpace();

    const floorLocked = Boolean(defaultFloorId) && !isUpdating;

    useEffect(() => {
        if (!isOpen) return;

        if (space) {
            setFormData({ ...space, floor: space.floor?._id || space.floor || '' });
        } else {
            const defaults = { ...INITIAL_FORM_DATA };
            defaults.status = OPERATIONAL_STATUSES.OPERATIONAL;
            defaults.securityType = SECURITY_TYPES.SHARED_FINGERPRINT;
            defaults.securityLevel = LEVELS.MEDIUM;
            if (defaultFloorId) {
                defaults.floor = defaultFloorId;
            }
            setFormData(defaults);
        }
    }, [isOpen, space, defaultFloorId]);

    const handleInputChange = async (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSelectChange = (name, value) => {
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleCheckBoxChange = async (e) => {
        const { name, checked } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleClose = () => {
        setFormData(INITIAL_FORM_DATA);
        onClose();
    };

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                floor: formData.floor,
                number: formData.number,
                code: formData.code,
                // capacity: formData.capacity,
                useableArea: formData.useableArea,
                totalArea: formData.totalArea,
                status: formData.status,
                securityType: formData.securityType,
                securityLevel: formData.securityLevel,
                isAccessible: formData.isAccessible,
                isBookable: formData.isBookable,
                dedicatedClimateControl: formData.dedicatedClimateControl,
                dedicatedInternet: formData.dedicatedInternet,
            };

            if (isUpdating) {
                await updateCoworkingSpaceMutation.mutateAsync({ coworkingSpaceId: space._id, data: payload });
            } else {
                await addCoworkingSpaceMutation.mutateAsync({ floorId: formData.floor, data: payload });
            }

            onSuccess?.();
        } catch (error) {
            console.log('error', error);
            NotifyError(error.response.data.message, error);
        } finally {
            setFormData(INITIAL_FORM_DATA);
            onClose();
        }
    };

    return (
        <ModalX
            isOpen={isOpen}
            onClose={handleClose}
            title={isUpdating ? "Update Space" : "Add New Space"}
            maxWidth="xxl"
        >
            <form onSubmit={handleFormSubmit}>
                <div className="space-y-2">
                    <div>
                        {isFloorsLoading ? <Spinner /> :
                            isFloorsError ? <div className="text-center py-5 text-red-500">Error loading floors. Please try again.</div> :
                                <Select
                                    label={<>Select Floor <span className="text-red-500 ml-1">*</span></>}
                                    value={formData.floor}
                                    disabled={isFloorsLoading || floorLocked}
                                    onChange={(newValue) => handleSelectChange("floor", newValue)}
                                >
                                    {floors?.map((floor, index) => (
                                        <Option key={index} value={floor._id}>
                                            <div className="flex justify-between gap-3 w-full">
                                                <div className="">
                                                    {getOrdinal(floor.number)} Floor - {floor.floorCode} in ({floor.building.name})
                                                </div>
                                                <div className="">
                                                    {floor.building.branch.name}
                                                </div>
                                            </div>
                                        </Option>
                                    ))}
                                </Select>
                        }
                    </div>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <Input
                            label="Space Number"
                            name="number"
                            value={formData.number}
                            onChange={handleInputChange}
                            required
                        />
                        <Input
                            label="Space Code"
                            name="code"
                            value={formData.code}
                            onChange={handleInputChange}
                            required
                        />
                    </div>

                    <div className="flex gap-2 flex-col md:flex-row">
                        <Input
                            label="Usable Area (sq/ft)"
                            name="useableArea"
                            type="number"
                            value={formData.useableArea}
                            onChange={handleInputChange}
                        />
                        <Input
                            label="Total Area (sq/ft)"
                            name="totalArea"
                            type="number"
                            value={formData.totalArea}
                            onChange={handleInputChange}
                        />
                    </div>
                    <div className="flex gap-2 flex-col md:flex-row">
                        <Select
                            label="Status"
                            value={formData.status}
                            onChange={newValue => handleSelectChange("status", newValue)}
                        >
                            {OPERATIONAL_STATUSES_ENUM.map((status, index) => (
                                <Option key={index} value={status}>
                                    {convertToPresentableText(status)}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            label="Security Level"
                            value={formData.securityLevel}
                            onChange={newValue => handleSelectChange("securityLevel", newValue)}
                        >
                            {LEVELS_ENUM.map((level, index) => (
                                <Option key={index} value={level}>
                                    {convertToPresentableText(level)}
                                </Option>
                            ))}
                        </Select>
                        <Select
                            label="Security Type"
                            value={formData.securityType}
                            onChange={newValue => handleSelectChange("securityType", newValue)}
                        >
                            {SECURITY_TYPES_ENUM.map((type, index) => (
                                <Option key={index} value={type}>
                                    {convertToPresentableText(type)}
                                </Option>
                            ))}
                        </Select>
                    </div>

                    <div className="">
                        <Checkbox
                            name="isBookable"
                            checked={formData.isBookable}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Is Bookable"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="isAccessible"
                            checked={formData.isAccessible}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Is Accessible"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="dedicatedInternet"
                            checked={formData.dedicatedInternet}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Dedicated Internet"
                            onChange={handleCheckBoxChange}
                        />
                        <Checkbox
                            name="dedicatedClimateControl"
                            checked={formData.dedicatedClimateControl}
                            className='h-4 w-4 p-0' iconProps={{ className: 'p-0' }} labelProps={{ className: 'p-0 text-xs' }}
                            label="Dedicated Climate Control"
                            onChange={handleCheckBoxChange}
                        />
                    </div>
                </div>
                <div className="mt-4 flex justify-end space-x-2">
                    <Button
                        onClick={handleClose}
                        className="rounded-full "
                        variant="text"
                    >
                        Cancel
                    </Button>
                    <Button type="submit" className="rounded-full">
                        {isUpdating ? "Update" : "Add"}
                    </Button>
                </div>
            </form>
        </ModalX>
    );
};

export default CoworkingCrudModal;
export { CoworkingCrudModal, INITIAL_FORM_DATA };
